
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext";
import { useCompanyContext } from "../hooks/useCompanyContext";
import { useAuthContext } from "../hooks/useAuthContext";
import "./Beheer.css";

const AddCompanyForm = () => {
  const { cultivationCompany } = useCultivationCompanyContext();
  const { company, dispatch } = useCompanyContext();
  const { user } = useAuthContext();

  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [companiesList, setCompaniesList] = useState([]);
  const [search, setSearch] = useState("");

  // Actieve klant selecteren
  const [selectingCompanyId, setSelectingCompanyId] = useState(null);
  const [selectionError, setSelectionError] = useState("");

  const token = localStorage.getItem("token");

  const sortCompaniesByCompanyIdDesc = (companies) => {
    return [...companies].sort((a, b) => {
      const numA = parseInt(String(a.Companyid).replace(/\D/g, ""), 10);
      const numB = parseInt(String(b.Companyid).replace(/\D/g, ""), 10);

      return numB - numA;
    });
  };

  const fetchCompanies = async () => {
    if (!cultivationCompany?._id) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/companies/by-cultivation/${cultivationCompany._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const companiesWithArchiveStatus = response.data.map((company) => ({
        ...company,
        archived: Boolean(company.archived),
      }));

      setCompaniesList(sortCompaniesByCompanyIdDesc(companiesWithArchiveStatus));
    } catch (error) {
      console.error("❌ Fout bij ophalen bedrijven:", error);
      setCompaniesList([]);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [cultivationCompany]);

  // WU-65: Zoeken op gedeeltelijke Company ID of bedrijfsnaam
  const filteredCompanies = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    if (!searchTerm) return companiesList;

    return companiesList.filter((company) =>
      String(company.Companyid ?? "")
        .toLowerCase()
        .includes(searchTerm) ||
      String(company.Name ?? "")
        .toLowerCase()
        .includes(searchTerm)
    );
  }, [companiesList, search]);

  // Actieve klant selecteren vanuit Beheer
  const handleCompanySelect = async (selectedCompanyId) => {
    if (!selectedCompanyId || !user?.email) return;

    setSelectingCompanyId(selectedCompanyId);
    setSelectionError("");

    const storageKey = `selectedCompany:${user.email.toLowerCase()}`;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/bedrijf/${encodeURIComponent(selectedCompanyId)}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token || token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const fullCompany = await response.json();

      if (!fullCompany?.Companyid) {
        throw new Error("Geen geldige bedrijfsgegevens ontvangen.");
      }

      // Actieve klant aanpassen in de context
      dispatch({
        type: "SET_COMPANY",
        payload: fullCompany,
      });

      // Selectie bewaren, identiek aan Navbar.js
      const minimal = {
        Companyid: fullCompany.Companyid,
        Name: fullCompany.Name,
      };

      sessionStorage.setItem(storageKey, JSON.stringify(minimal));
      sessionStorage.setItem("selectedCompany", JSON.stringify(minimal));

      console.log(
        "✅ Actieve klant gewijzigd:",
        fullCompany.Companyid,
        fullCompany.Name
      );
    } catch (error) {
      console.error("❌ Fout bij selecteren bedrijf:", error);
      setSelectionError("Het selecteren van deze klant is mislukt.");
    } finally {
      setSelectingCompanyId(null);
    }
  };

  const handleToggleArchived = async (companyId) => {
    const company = companiesList.find((c) => c.Companyid === companyId);

    if (!company) return;

    const newArchivedValue = !company.archived;

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/companies/archive/${companyId}`,
        {
          archived: newArchivedValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCompaniesList((prevCompanies) =>
        prevCompanies.map((company) =>
          company.Companyid === companyId
            ? {
                ...company,
                archived: response.data.archived,
              }
            : company
        )
      );
    } catch (error) {
      console.error("❌ Fout bij archiveren bedrijf:", error);
      setMessage("Fout bij archiveren van bedrijf.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!token) {
      setMessage("Geen geldige sessie, log opnieuw in.");
      setLoading(false);
      return;
    }

    if (!user?.email) {
      setMessage("Gebruiker niet gevonden. Log opnieuw in.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/addcompany`,
        {
          companyId,
          cultivationCompany: cultivationCompany?._id,
          createdBy: user.email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage(response.data.message || "Bedrijf succesvol toegevoegd!");
      setCompanyId("");
      fetchCompanies();
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Fout bij toevoegen van bedrijf."
      );
    }

    setLoading(false);
  };

  // Opmaak klikbare klantgegevens
  const companyLinkStyle = {
    background: "none",
    border: "none",
    padding: 0,
    color: "inherit",
    font: "inherit",
    textUnderlineOffset: "3px",
    textAlign: "left",
  };

  return (
    <div className="add-company">
      <h3 className="pageheading">Nieuw bedrijf toevoegen</h3>

      {message && <p>{message}</p>}

      <form onSubmit={handleSubmit}>
        <div>
          <label>Company ID: </label>
          <input
            type="text"
            placeholder="D123456..."
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <button type="submit" disabled={loading}>
            {loading ? "Toevoegen..." : "Bedrijf toevoegen"}
          </button>
        </div>
      </form>

      <div className="companies-overview">
        <h3 className="pageheading">Huidige bedrijven in systeem</h3>

        {/* WU-65: Zoekveld voor Company ID en Naam */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Zoeken op Company ID of naam..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>

        {selectionError && <p>{selectionError}</p>}

        {filteredCompanies.length > 0 ? (
          <table className="companies-table">
            <thead>
              <tr>
                <th>Company ID</th>
                <th>Naam</th>
                <th>Gearchiveerd</th>
              </tr>
            </thead>

            <tbody>
              {filteredCompanies.map((item) => {
                const isActive =
                  String(company?.Companyid) ===
                  String(item.Companyid);

                const isSelecting =
                  selectingCompanyId === item.Companyid;

                const disabled =
                  item.archived || selectingCompanyId !== null;

                return (
                  <tr key={item.Companyid}>
                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          handleCompanySelect(item.Companyid)
                        }
                        disabled={disabled}
                        style={{
                          ...companyLinkStyle,
                          fontWeight: isActive ? "bold" : "normal",
                          textDecoration: disabled ? "none" : "underline",
                          cursor: disabled ? "default" : "pointer",
                          opacity: 1,
                        }}
                        title="Selecteer deze klant"
                      >
                        {item.Companyid}
                      </button>
                    </td>

                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          handleCompanySelect(item.Companyid)
                        }
                        disabled={disabled}
                        style={{
                          ...companyLinkStyle,
                          fontWeight: isActive ? "bold" : "normal",
                          textDecoration: disabled ? "none" : "underline",
                          cursor: disabled ? "default" : "pointer",
                          opacity: 1,
                        }}
                        title="Selecteer deze klant"
                      >
                        {item.Name}
                      </button>

                      {isSelecting && (
                        <span style={{ marginLeft: "8px" }}>
                          Selecteren...
                        </span>
                      )}
                    </td>

                    <td>
                      <input
                        type="checkbox"
                        checked={item.archived || false}
                        onChange={() =>
                          handleToggleArchived(item.Companyid)
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p>
            {companiesList.length === 0
              ? "Geen bedrijven gevonden."
              : "Geen bedrijven gevonden die overeenkomen met de zoekopdracht."}
          </p>
        )}
      </div>
    </div>
  );
};

export default AddCompanyForm;
